import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Gavel, Download } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthToken } from '../../lib/authToken';

const PRIVILEGE_TYPES = [
  { value: 'attorney_client', label: 'Attorney–Client' },
  { value: 'work_product', label: 'Work Product' },
  { value: 'common_interest', label: 'Common Interest' },
  { value: 'other', label: 'Other' },
] as const;

const STATUS_OPTIONS = [
  { value: 'withheld', label: 'Withheld' },
  { value: 'partial', label: 'Partially Produced' },
  { value: 'produced', label: 'Produced' },
  { value: 'pending', label: 'Pending Review' },
] as const;

export interface PrivilegeLogEntry {
  id: string;
  description?: string | null;
  privilegeType?: string | null;
  author?: string | null;
  recipients?: string | null;
  batesRange?: string | null;
  documentDate?: string | null;
  status?: string | null;
  createdBy?: string | null;
  creator?: {
    firstName?: string;
    lastName?: string;
  } | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface PrivilegeLogPanelProps {
  caseId: string;
}

const emptyForm = () => ({
  description: '',
  privilegeType: 'attorney_client',
  author: '',
  recipients: '',
  batesRange: '',
  documentDate: '',
  status: 'pending',
});

const labelFor = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value?: string | null
) => options.find((o) => o.value === value)?.label || value || '—';

const formatDocDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return format(parseISO(String(value).slice(0, 10)), 'MMM d, yyyy');
  } catch {
    return value;
  }
};

const PrivilegeLogPanel: React.FC<PrivilegeLogPanelProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canRead = Boolean(
    user?.role === 'admin' || user?.permissions?.['privilegeLog:read']
  );
  const canWrite = Boolean(
    user?.role === 'admin' || user?.permissions?.['privilegeLog:write']
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PrivilegeLogEntry | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<PrivilegeLogEntry | null>(null);

  const { data: entries = [], isLoading, error } = useQuery({
    queryKey: ['privilege-logs', caseId],
    enabled: Boolean(caseId) && canRead,
    queryFn: async (): Promise<PrivilegeLogEntry[]> => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const res = await fetch(`/api/cases/${caseId}/privilege-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to load privilege log.'));
      }
      const json = await res.json();
      const list = json.data?.logs ?? json.data?.entries ?? [];
      return Array.isArray(list) ? list : [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const payload = {
        description: form.description.trim() || null,
        privilegeType: form.privilegeType || null,
        author: form.author.trim() || null,
        recipients: form.recipients.trim() || null,
        batesRange: form.batesRange.trim() || null,
        documentDate: form.documentDate || null,
        status: form.status || null,
      };

      const url = editing
        ? `/api/cases/${caseId}/privilege-logs/${editing.id}`
        : `/api/cases/${caseId}/privilege-logs`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(
          await getApiErrorMessage(
            res,
            editing ? 'Unable to update privilege log entry.' : 'Unable to add privilege log entry.'
          )
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privilege-logs', caseId] });
      toast.success(editing ? 'Entry updated' : 'Entry added');
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm());
    },
    onError: (err) => {
      toast.error(toUserErrorMessage(err, 'Unable to save privilege log entry.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const res = await fetch(`/api/cases/${caseId}/privilege-logs/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to delete privilege log entry.'));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privilege-logs', caseId] });
      toast.success('Entry deleted');
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(toUserErrorMessage(err, 'Unable to delete privilege log entry.'));
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (entry: PrivilegeLogEntry) => {
    setEditing(entry);
    setForm({
      description: entry.description || '',
      privilegeType: entry.privilegeType || 'attorney_client',
      author: entry.author || '',
      recipients: entry.recipients || '',
      batesRange: entry.batesRange || '',
      documentDate: entry.documentDate ? String(entry.documentDate).slice(0, 10) : '',
      status: entry.status || 'pending',
    });
    setDialogOpen(true);
  };

  const canSave = useMemo(() => Boolean(form.description.trim()), [form.description]);

  if (!canRead) {
    return (
      <Alert severity="info">
        Privilege logs are restricted to attorneys and administrators (work-product tier).
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Unable to load privilege log. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Privilege log</Typography>
          <Typography variant="body2" color="text.secondary">
            Track withheld and partially produced documents for discovery responses.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={async () => {
              try {
                const res = await fetch(
                  `/api/cases/${caseId}/privilege-logs?export=csv`,
                  {
                    headers: {
                      Authorization: `Bearer ${getAuthToken()}`,
                    },
                  }
                );
                if (!res.ok) {
                  throw new Error(await getApiErrorMessage(res, 'Export failed'));
                }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `privilege-log-${caseId}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Privilege log CSV downloaded');
              } catch (err) {
                toast.error(toUserErrorMessage(err, 'Export failed'));
              }
            }}
          >
            Export CSV
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add entry
            </Button>
          )}
        </Box>
      </Box>

      {entries.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Gavel sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No privilege log entries
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Document privileged materials and Bates ranges for opposing counsel.
            </Typography>
            {canWrite && (
              <Button variant="outlined" startIcon={<Add />} onClick={openCreate}>
                Add entry
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Bates range</TableCell>
                <TableCell>Doc date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap title={entry.description || ''}>
                      {entry.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={labelFor(PRIVILEGE_TYPES, entry.privilegeType)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{entry.author || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 120 }}>
                    <Typography variant="body2" noWrap title={entry.recipients || ''}>
                      {entry.recipients || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {entry.batesRange || '—'}
                  </TableCell>
                  <TableCell>{formatDocDate(entry.documentDate)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={labelFor(STATUS_OPTIONS, entry.status)}
                      color={entry.status === 'withheld' ? 'warning' : entry.status === 'produced' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.creator
                        ? `${entry.creator.firstName || ''} ${entry.creator.lastName || ''}`.trim() || '—'
                        : entry.createdBy
                          ? `${String(entry.createdBy).slice(0, 8)}…`
                          : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {entry.createdAt
                      ? format(parseISO(entry.createdAt), 'MMM d, yyyy')
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {canWrite && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(entry)} aria-label="Edit entry">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(entry)}
                          aria-label="Delete entry"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit privilege log entry' : 'Add privilege log entry'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Privilege type</InputLabel>
                <Select
                  label="Privilege type"
                  value={form.privilegeType}
                  onChange={(e) => setForm((p) => ({ ...p, privilegeType: e.target.value }))}
                >
                  {PRIVILEGE_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Author"
                value={form.author}
                onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recipients"
                value={form.recipients}
                onChange={(e) => setForm((p) => ({ ...p, recipients: e.target.value }))}
                placeholder="Comma-separated names or roles"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bates range"
                value={form.batesRange}
                onChange={(e) => setForm((p) => ({ ...p, batesRange: e.target.value }))}
                placeholder="PROD-000001 – PROD-000015"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Document date"
                InputLabelProps={{ shrink: true }}
                value={form.documentDate}
                onChange={(e) => setForm((p) => ({ ...p, documentDate: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSave || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add entry'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete privilege log entry?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the entry from the privilege log. This action cannot be undone.
          </Typography>
          {deleteTarget?.description && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {deleteTarget.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivilegeLogPanel;
